/**
 * Device preferences.
 *
 * Deliberately a separate store from `scorekeeper`: these belong to the phone,
 * not to a game, and they must never ride along in the sync payload.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * The four looks. `speels` is the default: it is the only one whose style the
 * twelve drawn animal avatars already speak.
 */
export const THEMES = ['speels', 'nu', 'arcade', 'kaartkamer', 'poly', 'pixel'] as const;
export type Theme = (typeof THEMES)[number];

export const THEME_LABELS: Record<Theme, { name: string; hint: string }> = {
  speels: { name: 'Speels', hint: 'vlakke kleuren, dikke lijnen' },
  nu: { name: 'Rustig', hint: 'donkerblauw met groen' },
  arcade: { name: 'Arcade', hint: 'neon en scanlijnen' },
  kaartkamer: { name: 'Kaartkamer', hint: 'groen laken en goud' },
  poly: { name: 'Poly', hint: 'facetten, hoekig lettertype' },
  pixel: { name: 'Pixel', hint: '8-bit, met echt pixellettertype' },
};

export const DEFAULT_THEME: Theme = 'speels';

export interface DeviceSettings {
  sound: boolean;
  vibration: boolean;
  theme: Theme;
}

interface SettingsActions {
  setSound: (sound: boolean) => void;
  setVibration: (vibration: boolean) => void;
  setTheme: (theme: Theme) => void;
}

export const useSettings = create<DeviceSettings & SettingsActions>()(
  persist(
    (set) => ({
      // Both on: this is a game, and a mute control nobody discovers is worse
      // than one they have to turn off once.
      sound: true,
      vibration: true,
      theme: DEFAULT_THEME,
      setSound: (sound) => set({ sound }),
      setVibration: (vibration) => set({ vibration }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: 'boerenbridge.settings',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      /*
       * A phone that stored its settings before themes existed has no `theme`.
       * Without this it would come back `undefined`, `data-theme` would be the
       * string "undefined", and every colour variable would fall through to the
       * `:root` block — which happens to be right, but only by accident.
       */
      migrate: (persisted, fromVersion) => {
        const stored = persisted as Partial<DeviceSettings>;
        if (fromVersion >= 2 && stored.theme && THEMES.includes(stored.theme)) return stored;
        return { ...stored, theme: DEFAULT_THEME };
      },
      onRehydrateStorage: () => (state) => {
        // localStorage is read after the first paint, so re-apply once it is in.
        if (state?.theme) applyTheme(state.theme);
      },
    }
  )
);

/**
 * Put the theme on the document.
 *
 * On `<html>` rather than a React wrapper, so `body` and anything portalled
 * outside the app tree is covered too — and so the inline script in index.html
 * can set it before the first paint.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

/** Non-reactive read, for the feedback layer which runs outside React. */
export function currentSettings(): DeviceSettings {
  const { sound, vibration, theme } = useSettings.getState();
  return { sound, vibration, theme };
}
